import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { colored } from '../../Style.js';

// Sci-fi unit palette: pale alloy armour, dark gunmetal frames, cyan sensors.
// Team colour goes on the big armour panels and on unlit glow trim.
export const SCIFI = {
  white: 0xe6ebf1,
  alloy: 0xb4bdc8,
  alloyDark: 0x6b7480,
  gunmetal: 0x353b45,
  black: 0x1b1f26,
  cyan: 0x8af6ff,
  gold: 0xf0c25a,
  neutralPanel: 0x9aa3ad,
  neutralGlow: 0xdfe6ee,
  dead: 0x2a2d33
};

const cache = new Map();

function cached(key, unique, make) {
  if (!unique && cache.has(key)) return cache.get(key);
  const material = make();
  if (!unique) cache.set(key, material);
  return material;
}

// Lit armour: flat-shaded, slightly metallic, coloured per vertex.
// Shared unless `unique` (needed when one instance changes its opacity).
export function armor({ unique = false } = {}) {
  return cached('armor', unique, () => new THREE.MeshStandardMaterial({
    color: 0xffffff,
    vertexColors: true,
    flatShading: true,
    metalness: 0.35,
    roughness: 0.5
  }));
}

// Unlit trim that reads as glowing without needing any lights, coloured per vertex.
// Skipping tone mapping keeps team colours saturated instead of washing to pastel.
export function glow({ unique = false } = {}) {
  return cached('glow', unique, () => glowMaterial({ vertexColors: true }));
}

export function glowMaterial(params = {}) {
  const material = new THREE.MeshBasicMaterial({ color: 0xffffff, ...params });
  material.toneMapped = false;
  return material;
}

// Soft additive pool of light on the ground, shared per colour.
export function haze(color) {
  const c = new THREE.Color(color);
  return cached(`haze|${c.getHexString()}`, false, () => new THREE.MeshBasicMaterial({
    color: c,
    transparent: true,
    opacity: 0.35,
    blending: THREE.AdditiveBlending,
    depthWrite: false
  }));
}

// The bright version of a team colour used for glowing trim.
export function glowColor(color, boost = 0.06) {
  return new THREE.Color(color).offsetHSL(0, 0, boost);
}

export function hexColor(color) {
  return typeof color === 'string' ? parseInt(color.replace('#', ''), 16) : color;
}

// Merge baked parts into one geometry and free the pieces.
export function bake(parts) {
  const geometry = mergeGeometries(parts);
  parts.forEach(p => p.dispose());
  return geometry;
}

// A box stretched between two points, for struts, arms and legs.
export function beam(from, to, thickness, color) {
  const a = new THREE.Vector3(...from);
  const b = new THREE.Vector3(...to);
  const dir = b.clone().sub(a);
  const geo = colored(new THREE.BoxGeometry(thickness, thickness, dir.length()), color);
  const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir.normalize());
  geo.applyMatrix4(new THREE.Matrix4().compose(a.add(b).multiplyScalar(0.5), quat, new THREE.Vector3(1, 1, 1)));
  return geo;
}

// A side profile given as [z, y] points, extruded across X and centred: wedge hulls.
export function prism(points, width) {
  const shape = new THREE.Shape(points.map(([z, y]) => new THREE.Vector2(z, y)));
  const geometry = new THREE.ExtrudeGeometry(shape, { depth: width, bevelEnabled: false });
  geometry.rotateY(-Math.PI / 2);
  geometry.translate(width / 2, 0, 0);
  return geometry;
}

// Walk-cycle bookkeeping shared by the troopers: how fast the rendered mesh is moving.
export class Gait {
  constructor(mesh) {
    this.mesh = mesh;
    this.walk = 0;
    this.phase = 0;
    this.lastTime = null;
    this.last = mesh.position.clone();
  }

  // Returns the frame time, or 0 on the first call. `speed` is the run speed in units/s.
  step(speed, stride) {
    const now = performance.now() / 1000;
    const dt = this.lastTime === null ? 0 : Math.min(now - this.lastTime, 0.1);
    this.lastTime = now;
    if (dt <= 0) return 0;
    const pos = this.mesh.position;
    // Clamp so a respawn teleport doesn't register as a sprint
    const moved = Math.min(Math.hypot(pos.x - this.last.x, pos.z - this.last.z), 1);
    this.last.copy(pos);
    const target = Math.min(1, moved / dt / speed);
    this.walk += (target - this.walk) * Math.min(1, dt * 8);
    this.phase += moved * stride;
    return dt;
  }
}
